import { generatePath, useLocation, useParams } from 'react-router-dom';

import { K8sProviderIconStatus } from '@cast/features-cluster-ui';
import { Icons } from '@cast/design-system';
import { useNavigate } from '@cast/infrastructure-organization';
import {
  useClusters,
  useClusterQuery,
} from '@cast/shared-cluster-context-data-access';
import { Cluster } from '@cast/types';

import { HeaderSelect } from './_components/HeaderSelect';
import { filterSelectableClusters } from './filterSelectableClusters';
import { HeaderSelectConfig } from './types';

const DEFAULT_PATH_TEMPLATE = '/automation/external-clusters/:clusterId';

type BuildLinkArgs = {
  location: ReturnType<typeof useLocation>;
  activeId?: string;
  cluster: Cluster;
};

const buildLink = ({ location, activeId, cluster }: BuildLinkArgs) => {
  if (activeId && location.pathname.includes(activeId)) {
    return location.pathname.replace(activeId, cluster.id);
  }

  return generatePath(DEFAULT_PATH_TEMPLATE, { clusterId: cluster.id });
};

export const ClusterSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clusterId } = useParams<{ clusterId: string }>();
  const { cluster: currentCluster } = useClusterQuery(clusterId);
  const { clusters, isLoading, hasError, refetch } = useClusters({
    polling: false,
  });

  const currentClusterId = currentCluster?.id;
  const options = filterSelectableClusters(clusters).map((cluster) => ({
    item: cluster,
    selected: cluster.id === currentClusterId,
  }));

  const config: HeaderSelectConfig<Cluster> = {
    options,
    isLoading,
    hasError,
    refetch,
    getItemId: ({ id }) => id,
    getItemName: ({ name }) => name,
    getItemSecondaryText: ({ providerNamespaceId }) => providerNamespaceId,
    renderItemIcon: (cluster) => (
      <K8sProviderIconStatus cluster={cluster} size={14} />
    ),
    renderTriggerIcon: (cluster) => (
      <K8sProviderIconStatus cluster={cluster} size={20} />
    ),
    getSearchableText: ({ id, name, providerNamespaceId }) => [
      name,
      providerNamespaceId ?? '',
      id,
    ],
    onSelect: (c) =>
      navigate(buildLink({ location, activeId: currentClusterId, cluster: c })),
    testIdPrefix: 'cluster-chooser',
    searchPlaceholder: 'Enter cluster name',
    emptyTitle: 'No clusters',
    emptySubtitle: 'Connect a cluster to get started',
    notFoundTitle: 'No clusters found',
    notFoundSubtitle: 'Adjust your search criteria',
    errorTitle: 'Failed to load clusters',
    defaultTriggerLabel: 'Select cluster',
    emptyIcon: <Icons.Nut size={14} />,
  };

  return <HeaderSelect config={config} />;
};
